import { useMemo } from "react";
import { cn } from "../../utilities/helpers";
import { useCurrency } from "../dropdown";
import { buildReturnContributions } from "./analytics";
import {
	formatCurrency,
	formatSignedCurrency,
	formatSignedPercent,
	useAssetPalette,
	useInvestmentSummaryQuery,
} from "./shared";

// The takeaway names the fewest winners whose gains cross this much of the
// gross gain. Four-fifths carries the point -- "these are what made the money"
// -- without the sentence degenerating into a list of every position.
const LEADER_SHARE = 0.8;

// Below half a cent a figure rounds to zero in every formatter here, so it is
// treated as flat rather than drawn as a one-pixel bar on either side.
const MONEY_EPSILON = 0.005;

const toneClass = (value) => {
	if (value > MONEY_EPSILON) return "text-emerald-700";
	if (value < -MONEY_EPSILON) return "text-rose-700";
	return "text-slate-600";
};

const joinNames = (names) =>
	names.length > 1
		? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`
		: names[0] || "";

const Bar = ({ className, width }) => (
	<div className={cn("h-2", className)} style={{ width: `${width}%` }} />
);

const Contribution = () => {
	const { currency } = useCurrency();
	const paletteFor = useAssetPalette();
	const { data: summary, isLoading } = useInvestmentSummaryQuery();

	const { contributions, totals } = useMemo(
		() => buildReturnContributions(summary?.assets),
		[summary]
	);

	const takeaway = useMemo(() => {
		if (!contributions.length) return null;

		const drag =
			totals.losers > 0
				? { count: totals.losers, amount: Math.abs(totals.totalNegative) }
				: null;

		// Every position is flat or under water, so there is no gain to
		// apportion: `sharePercent` is 0 on every row and quoting it would claim
		// each holding produced none of a gain that does not exist.
		if (totals.totalPositive <= 0) return { hasGains: false, drag };

		const leaders = [];
		let covered = 0;
		for (const row of contributions) {
			if (row.totalReturn <= 0) break;
			leaders.push(row.label);
			covered += row.totalReturn;
			if (covered >= totals.totalPositive * LEADER_SHARE) break;
		}

		return {
			hasGains: true,
			names: joinNames(leaders),
			count: leaders.length,
			share: (covered / totals.totalPositive) * 100,
			drag,
		};
	}, [contributions, totals]);

	// One scale for both directions, anchored on the largest move either way, so
	// a loser half the size of the top winner draws half the bar.
	const maxAbsReturn = contributions.reduce(
		(max, row) => Math.max(max, Math.abs(row.totalReturn)),
		0
	);

	return (
		<section className="rounded-xl border border-slate-200 bg-white shadow-sm">
			<div className="p-4 pb-3 md:px-6 md:pt-5">
				<h2 className="text-lg font-semibold text-slate-900">Contribution to return</h2>
				<p className="text-sm text-slate-500">
					{contributions.length
						? `${formatSignedCurrency(totals.totalReturn, currency)} total return across ${
								contributions.length
						  } ${contributions.length === 1 ? "holding" : "holdings"}.`
						: "Which holdings produced the portfolio's gain."}
				</p>
			</div>

			<div className="px-4 pb-5 md:px-6">
				{isLoading && !summary && (
					<div className="space-y-3" aria-hidden="true">
						{Array.from({ length: 5 }).map((_, index) => (
							<div key={index} className="h-5 animate-pulse rounded bg-slate-100" />
						))}
					</div>
				)}

				{summary && !contributions.length && (
					<p className="py-6 text-center text-sm text-slate-500">
						No holdings to attribute returns to yet.
					</p>
				)}

				{takeaway && (
					<p className="mb-4 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
						{takeaway.hasGains ? (
							<>
								<span className="font-medium text-slate-900">{takeaway.names}</span>{" "}
								produced{" "}
								<span className="font-medium tabular-nums text-slate-900">
									{takeaway.share.toFixed(1)}%
								</span>{" "}
								of all gains
							</>
						) : takeaway.drag ? (
							<>
								Nothing is in profit yet:{" "}
								<span className="font-medium tabular-nums text-slate-900">
									{takeaway.drag.count}
								</span>{" "}
								{takeaway.drag.count === 1 ? "holding is" : "holdings are"} down
							</>
						) : (
							<>No gains or losses to attribute yet</>
						)}
						{takeaway.drag && (
							<>
								{takeaway.hasGains ? (
									<>
										;{" "}
										<span className="font-medium tabular-nums text-slate-900">
											{takeaway.drag.count}
										</span>{" "}
										{takeaway.drag.count === 1 ? "holding" : "holdings"} gave back{" "}
									</>
								) : (
									" "
								)}
								<span className="font-medium tabular-nums text-rose-700">
									{formatCurrency(takeaway.drag.amount, currency)}
								</span>
							</>
						)}
						.
					</p>
				)}

				<div className="relative">
					{/* The shared centre line doubles as the zero axis every bar grows from. */}
					{contributions.length > 0 && (
						<span
							aria-hidden="true"
							className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-slate-200"
						/>
					)}

					<ul className="space-y-1">
						{contributions.map((row) => {
							const palette = paletteFor(row.asset);
							const isGain = row.totalReturn > MONEY_EPSILON;
							const isLoss = row.totalReturn < -MONEY_EPSILON;
							const width = maxAbsReturn
								? Math.max((Math.abs(row.totalReturn) / maxAbsReturn) * 100, 1.5)
								: 0;

							return (
								<li key={row.asset} className="py-1">
									<div className="flex items-baseline justify-between gap-3">
										<span className="flex min-w-0 items-center gap-2">
											<span
												aria-hidden="true"
												className={cn("h-2.5 w-2.5 shrink-0 rounded-full", palette.dot)}
											/>
											<span className="truncate text-sm font-medium text-slate-900">
												{row.label}
											</span>
										</span>
										<span className="flex shrink-0 items-baseline gap-3 text-sm tabular-nums">
											<span className={cn("font-medium", toneClass(row.totalReturn))}>
												{formatSignedCurrency(row.totalReturn, currency)}
											</span>
											{takeaway?.hasGains && (
												<span
													className="w-16 text-right text-slate-500"
													title="Share of the portfolio's gross gain"
												>
													{formatSignedPercent(row.sharePercent, 1)}
												</span>
											)}
										</span>
									</div>

									<div aria-hidden="true" className="mt-1 grid grid-cols-2">
										<div className="flex justify-end">
											{isLoss && <Bar className="rounded-l-full bg-rose-500" width={width} />}
										</div>
										<div className="flex justify-start">
											{isGain && (
												<Bar className="rounded-r-full bg-emerald-500" width={width} />
											)}
										</div>
									</div>
								</li>
							);
						})}
					</ul>
				</div>
			</div>
		</section>
	);
};

export default Contribution;
